import React, { useEffect, useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { House, Gamepad2, Users, Menu as MenuIcon, X as CloseIcon, BarChart3, User as UserIcon, Trophy, Medal, Palette, Settings as SettingsIcon, Sparkles, LogOut, PlayCircle, CircleHelp } from 'lucide-react';

import { useGame } from '../../context/GameContext';
import { useAuth } from '../../context/AuthContext';
import { useSurface } from '../../hooks/useSurface';
import { usePendingFriendRequests } from '../../hooks/usePendingFriendRequests';
import { WordRushMark } from './Logo';

/** Tabs. Order matters — left to right on the bar, top to bottom on the rail. */
type Tab = 'home' | 'solo' | 'game' | 'friends' | 'menu';

/**
 * Routes where the nav should be hidden. Auth flows hide because they're
 * modal. Game routes (lobby, active game, results) hide because they get
 * their own context-aware InGameNav — the user shouldn't be one tap from
 * leaving an active round to a different tab. THIS IS TRUE ON DESKTOP TOO:
 * a persistent rail beside a live round is a persistent invitation to
 * abandon your table.
 */
const HIDDEN_ROUTE_PREFIXES = [
  '/login', '/signup', '/forgot-password', '/setup', '/error',
  '/game', '/game-results', '/room/',
  // The television. `/tv/:code` is a STAGE, not a page: nobody is going to
  // tap "Friends" on a wall, and a 72px app rail down the side of a broadcast
  // is the single clearest tell that what you are looking at is a big phone.
  '/tv',
];

/** Decide which tab is active based on the URL. */
function activeFor(pathname: string): Tab | null {
  if (pathname.startsWith('/home') || pathname === '/') return 'home';
  if (pathname.startsWith('/solo')) return 'solo';
  if (pathname.startsWith('/game') || pathname.startsWith('/room/')) return 'game';
  if (pathname.startsWith('/friends')) return 'friends';
  return null;
}

const MENU_ITEMS = [
  { icon: UserIcon, label: 'Profile', path: '/profile' },
  { icon: BarChart3, label: 'Stats', path: '/stats' },
  { icon: Sparkles, label: 'Game history', path: '/history' },
  { icon: Trophy, label: 'Achievements', path: '/achievements' },
  { icon: Medal, label: 'Leaderboard', path: '/leaderboard' },
  { icon: Palette, label: 'Appearance', path: '/settings#display' },
  { icon: SettingsIcon, label: 'Settings', path: '/settings' },
  { icon: CircleHelp, label: 'How it works', path: '/how-it-works' },
  { icon: Sparkles, label: 'Account & subscription', path: '/plans' },
];

/**
 * THE app nav. One component, two forms:
 *
 *   < 1024   a docked bottom bar   (exactly what shipped; do not touch)
 *   ≥ 1024   a 72px icon rail on the left
 *
 * Same tabs, same `activeFor`, same hide rules, same menu drawer. The only
 * thing that changes is the axis. There is deliberately no second component
 * and no `DesktopNav` — a fork here is how the two surfaces start drifting
 * apart, and the whole point of this sprint is that they don't.
 *
 * Labels are drawn on the rail (13px, the information floor) rather than
 * hidden behind tooltips: hover must never be load-bearing.
 */
export function AppNav() {
  const navigate = useNavigate();
  const location = useLocation();
  const { state } = useGame();
  const { signOut } = useAuth();
  const surface = useSurface();
  const [menuOpen, setMenuOpen] = useState(false);
  const pendingRequests = usePendingFriendRequests();

  // `/` is the splash for a signed-in user and THE LANDING for everyone else.
  // Neither wants a nav: three of the five tabs are behind an auth gate, and
  // showing a stranger "Friends" before they've seen a single round is the
  // same mistake as the sign-in wall we just tore out. Exact match, obviously
  // — `startsWith('/')` would hide the nav on every route in the app.
  const hidden =
    location.pathname === '/' ||
    HIDDEN_ROUTE_PREFIXES.some(p => location.pathname.startsWith(p));
  const isRail = surface === 'desktop';

  /**
   * Signal visibility to `.app-shell` via a class on <html>.
   *
   *   wr-with-bnav — reserve padding-BOTTOM for the docked bar (base layer)
   *   wr-with-rail — reserve padding-LEFT for the rail  (desktop layer only)
   *
   * Exactly one of them, ever. The bnav class is untouched phone behaviour;
   * the rail class is only ever read from inside a min-width query.
   */
  useEffect(() => {
    if (typeof document === 'undefined') return;
    const root = document.documentElement;
    root.classList.toggle('wr-with-bnav', !hidden && !isRail);
    root.classList.toggle('wr-with-rail', !hidden && isRail);
    return () => {
      root.classList.remove('wr-with-bnav');
      root.classList.remove('wr-with-rail');
    };
  }, [hidden, isRail]);

  if (hidden) return null;

  const active = activeFor(location.pathname);

  // Active-game detection — used for both routing and the dot indicator.
  const inGame = !!state.roomCode && (state.currentScreen === 'game' || state.currentScreen === 'lobby');

  const tabRoutes: Record<Tab, string> = {
    home: '/home',
    solo: '/solo',
    game: inGame
      ? (state.currentScreen === 'lobby' ? `/room/${state.roomCode}` : `/game/${state.roomCode}`)
      : '/home',
    friends: '/friends',
    menu: '#menu', // sentinel — opens drawer instead of navigating
  };

  const goto = (tab: Tab) => {
    if (tab === 'menu') {
      setMenuOpen(true);
      return;
    }
    navigate(tabRoutes[tab]);
  };

  /**
   * The Friends icon, with a count of people waiting on you.
   *
   * This badge is the piece the friends feature never had, and its absence is
   * most of why "add friend" looked like it did nothing: a request landed in
   * Firestore correctly, but the ONLY surface that showed it was a tab inside
   * the Friends screen, which nothing pointed at. The recipient never found out,
   * never accepted, and so the sender's list never changed. A request nobody can
   * see is the same as a request that was never sent.
   */
  const friendsIcon = (
    <span style={{ position: 'relative', display: 'inline-flex' }}>
      <Users size={20} strokeWidth={2.2} />
      {pendingRequests > 0 && (
        <span
          aria-hidden
          style={{
            position: 'absolute',
            top: -6,
            right: -8,
            minWidth: 16,
            height: 16,
            padding: '0 4px',
            borderRadius: 8,
            background: 'var(--accent)',
            color: '#fff',
            fontFamily: 'var(--ui)',
            fontSize: 10,
            fontWeight: 800,
            lineHeight: '16px',
            textAlign: 'center',
            boxShadow: '0 0 0 2px var(--surface)',
          }}
        >
          {pendingRequests > 9 ? '9+' : pendingRequests}
        </span>
      )}
    </span>
  );
  const friendsAria = pendingRequests > 0
    ? `Friends, ${pendingRequests} pending ${pendingRequests === 1 ? 'request' : 'requests'}`
    : 'Friends';

  /* The live-game dot. Same markup in both forms. */
  const liveDot = (
    <span style={{ position: 'relative', display: 'inline-flex' }}>
      <Gamepad2 size={20} strokeWidth={2.2} />
      <span
        aria-hidden
        style={{
          position: 'absolute',
          top: -2,
          right: -4,
          width: 8,
          height: 8,
          borderRadius: '50%',
          background: 'var(--accent)',
          boxShadow: '0 0 0 2px var(--surface)',
        }}
      />
    </span>
  );

  return (
    <>
      {isRail ? (
        <nav className="wr-rail" role="navigation" aria-label="Main">
          <button
            type="button"
            className="rail-mark"
            onClick={() => navigate('/home')}
            aria-label="WordRush home"
            title="WordRush"
          >
            <WordRushMark size={28} />
          </button>

          <button className={`item ${active === 'home' ? 'active' : ''}`} onClick={() => goto('home')} type="button" title="Home">
            <House size={20} strokeWidth={2.2} />
            <span>Home</span>
          </button>
          <button className={`item ${active === 'solo' ? 'active' : ''}`} onClick={() => goto('solo')} type="button" title="Play">
            <PlayCircle size={20} strokeWidth={2.2} />
            <span>Play</span>
          </button>
          {inGame && (
            <button
              className={`item ${active === 'game' ? 'active' : ''}`}
              onClick={() => goto('game')}
              type="button"
              title="Return to active game"
              aria-label="Return to active game"
            >
              {liveDot}
              <span>Live</span>
            </button>
          )}
          <button
            className={`item ${active === 'friends' ? 'active' : ''}`}
            onClick={() => goto('friends')}
            type="button"
            title="Friends"
            aria-label={friendsAria}
          >
            {friendsIcon}
            <span>Friends</span>
          </button>

          <span className="rail-spacer" />

          <button className="item" onClick={() => goto('menu')} type="button" title="Menu" aria-label="Menu">
            <MenuIcon size={20} strokeWidth={2.2} />
            <span>Menu</span>
          </button>
        </nav>
      ) : (
        <nav
          className="bnav"
          role="navigation"
          aria-label="Main"
          style={{
            position: 'fixed',
            left: 0,
            right: 0,
            bottom: 0,
            zIndex: 'var(--z-nav, 50)',
          }}
        >
          <button className={`item ${active === 'home' ? 'active' : ''}`} onClick={() => goto('home')} type="button">
            <House size={20} strokeWidth={2.2} />
            <span>Home</span>
          </button>
          <button className={`item ${active === 'solo' ? 'active' : ''}`} onClick={() => goto('solo')} type="button" aria-label="Play">
            <PlayCircle size={20} strokeWidth={2.2} />
            <span>Play</span>
          </button>
          {/* Game tab — only visible while a game is in progress. When no
              game is live, it'd just route to /home (duplicate of the Home
              tab), so we hide it entirely to keep the nav clean. */}
          {inGame && (
            <button className={`item ${active === 'game' ? 'active' : ''}`} onClick={() => goto('game')} type="button" aria-label="Return to active game">
              {liveDot}
              <span>Live</span>
            </button>
          )}
          <button
            className={`item ${active === 'friends' ? 'active' : ''}`}
            onClick={() => goto('friends')}
            type="button"
            aria-label={friendsAria}
          >
            {friendsIcon}
            <span>Friends</span>
          </button>
          <button className="item" onClick={() => goto('menu')} type="button" aria-label="Menu">
            <MenuIcon size={20} strokeWidth={2.2} />
            <span>Menu</span>
          </button>
        </nav>
      )}

      {/* Drawer menu — surfaces secondary destinations the nav doesn't host
          (Profile, Settings) plus discovery targets (Stats, History,
          Achievements). Identical on both surfaces; the sheet just centres
          itself instead of docking to the bottom edge on a desktop. */}
      {menuOpen && (
        <div
          role="dialog"
          aria-modal="true"
          aria-label="Menu"
          onClick={() => setMenuOpen(false)}
          style={{
            position: 'fixed',
            inset: 0,
            zIndex: 'var(--z-dialog, 200)',
            background: 'rgba(0,0,0,0.5)',
            backdropFilter: 'blur(6px)',
            display: 'flex',
            alignItems: isRail ? 'center' : 'flex-end',
            justifyContent: 'center',
          }}
        >
          <div
            onClick={e => e.stopPropagation()}
            className="card lg"
            style={{
              width: 'calc(100% - 32px)',
              maxWidth: 460,
              margin: 16,
              background: 'var(--surface)',
              padding: 20,
              maxHeight: '85vh',
              overflowY: 'auto',
            }}
          >
            <div className="wr-row" style={{ alignItems: 'center', justifyContent: 'space-between', marginBottom: 12 }}>
              <span className="eyebrow">Menu</span>
              <button
                onClick={() => setMenuOpen(false)}
                aria-label="Close menu"
                type="button"
                className="wr-center"
                // 44×44, not a bare 20px glyph — this is a primary escape
                // hatch and it has to be hittable with a thumb.
                style={{ width: 44, height: 44, margin: -10, background: 'transparent', border: 'none', cursor: 'pointer', color: 'var(--ink-3)' }}
              >
                <CloseIcon size={20} />
              </button>
            </div>
            <div className="wr-col gap-1">
              {MENU_ITEMS.map(item => {
                const Icon = item.icon;
                return (
                  <button
                    key={item.label}
                    onClick={() => {
                      setMenuOpen(false);
                      navigate(item.path);
                    }}
                    type="button"
                    className="prow"
                    style={{ cursor: 'pointer', padding: '12px 14px' }}
                  >
                    <Icon size={20} color="var(--ink-2)" strokeWidth={2.2} />
                    <div className="body">
                      <div className="name" style={{ fontSize: 15 }}>{item.label}</div>
                    </div>
                  </button>
                );
              })}
              <button
                onClick={async () => {
                  setMenuOpen(false);
                  try { await signOut(); } catch (e) { console.warn('signOut failed', e); }
                  navigate('/login');
                }}
                type="button"
                className="prow"
                style={{ cursor: 'pointer', padding: '12px 14px', marginTop: 8 }}
              >
                <LogOut size={20} color="var(--coral, #b94a3b)" strokeWidth={2.2} />
                <div className="body">
                  <div className="name" style={{ fontSize: 15, color: 'var(--coral, #b94a3b)' }}>Sign out</div>
                </div>
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
